import React, { useState } from 'react'
import { Upload, X, File } from 'lucide-react'
import './FileInput.css'

/**
 * Reusable FileInput Component
 *
 * A customizable file upload input with drag-and-drop, preview, and validation
 *
 * @param {Object} props - Component props
 * @param {string} props.label - File input label
 * @param {string} props.helpText - Helper text
 * @param {Function} props.onChange - Change handler (receives File object)
 * @param {string} props.error - Error message
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {string} props.accept - Accepted file types (e.g., "image/*,.pdf")
 * @param {number} props.maxSize - Maximum file size in bytes
 * @param {boolean} props.multiple - Allow multiple files
 * @param {boolean} props.showPreview - Show file preview (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {React.Ref} ref - Forwarded ref for React Hook Form compatibility
 */
const FileInput = React.forwardRef(
	(
		{
			label,
			helpText,
			onChange,
			error,
			required = false,
			disabled = false,
			accept,
			maxSize,
			multiple = false,
			showPreview = true,
			className = '',
			id,
			name,
			...props
		},
		ref
	) => {
		const [selectedFiles, setSelectedFiles] = useState([])
		const [isDragging, setIsDragging] = useState(false)
		const [validationError, setValidationError] = useState(null)

		const fileInputId = id || `file-input-${name || label?.replace(/\s+/g, '-').toLowerCase()}`
		const baseClass = 'file-input'
		const wrapperClasses = [
			baseClass,
			error && `${baseClass}--error`,
			disabled && `${baseClass}--disabled`,
			isDragging && `${baseClass}--dragging`,
			className,
		]
			.filter(Boolean)
			.join(' ')

		// Validate file
		const validateFile = (file) => {
			if (maxSize && file.size > maxSize) {
				const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2)
				return `File size exceeds ${maxSizeMB}MB`
			}
			return null
		}

		// Handle file selection
		const handleFileChange = (files) => {
			const fileArray = Array.from(files)
			let hasError = false

			for (const file of fileArray) {
				const error = validateFile(file)
				if (error) {
					setValidationError(error)
					hasError = true
					break
				}
			}

			if (!hasError) {
				setValidationError(null)
				setSelectedFiles(fileArray)
				if (onChange) {
					onChange(multiple ? fileArray : fileArray[0])
				}
			}
		}

		// Handle input change
		const onInputChange = (e) => {
			if (e.target.files && e.target.files.length > 0) {
				handleFileChange(e.target.files)
			}
		}

		// Handle drag and drop
		const onDragOver = (e) => {
			e.preventDefault()
			if (!disabled) setIsDragging(true)
		}

		const onDragLeave = (e) => {
			e.preventDefault()
			setIsDragging(false)
		}

		const onDrop = (e) => {
			e.preventDefault()
			setIsDragging(false)
			if (!disabled && e.dataTransfer.files) {
				handleFileChange(e.dataTransfer.files)
			}
		}

		// Remove file
		const removeFile = (index) => {
			const newFiles = selectedFiles.filter((_, i) => i !== index)
			setSelectedFiles(newFiles)
			if (onChange) {
				onChange(multiple ? newFiles : null)
			}
		}

		// Format file size
		const formatFileSize = (bytes) => {
			if (bytes === 0) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
		}

		const displayError = error || validationError

		return (
			<div className={wrapperClasses}>
				{label && (
					<label htmlFor={fileInputId} className={`${baseClass}__label`}>
						{label}
						{required && <span className={`${baseClass}__required`}>*</span>}
					</label>
				)}

				{helpText && !displayError && (
					<span className={`${baseClass}__help-text`}>{helpText}</span>
				)}

				<div
					className={`${baseClass}__dropzone`}
					onDragOver={onDragOver}
					onDragLeave={onDragLeave}
					onDrop={onDrop}
				>
					<input
						id={fileInputId}
						type="file"
						name={name}
						accept={accept}
						multiple={multiple}
						disabled={disabled}
						required={required}
						className={`${baseClass}__input`}
						onChange={onInputChange}
						ref={ref}
						{...props}
					/>

					<div className={`${baseClass}__dropzone-content`}>
						<Upload className={`${baseClass}__icon`} size={32} />
						<div className={`${baseClass}__text`}>
							<span className={`${baseClass}__text-primary`}>
								Click to upload or drag and drop
							</span>
							{accept && (
								<span className={`${baseClass}__text-secondary`}>
									Accepted: {accept}
								</span>
							)}
							{maxSize && (
								<span className={`${baseClass}__text-secondary`}>
									Max size: {formatFileSize(maxSize)}
								</span>
							)}
						</div>
					</div>
				</div>

				{/* File Preview */}
				{showPreview && selectedFiles.length > 0 && (
					<div className={`${baseClass}__preview`}>
						{selectedFiles.map((file, index) => (
							<div key={index} className={`${baseClass}__preview-item`}>
								<File className={`${baseClass}__preview-icon`} size={20} />
								<div className={`${baseClass}__preview-info`}>
									<span className={`${baseClass}__preview-name`}>{file.name}</span>
									<span className={`${baseClass}__preview-size`}>
										{formatFileSize(file.size)}
									</span>
								</div>
								{!disabled && (
									<button
										type="button"
										className={`${baseClass}__preview-remove`}
										onClick={() => removeFile(index)}
										aria-label="Remove file"
									>
										<X size={16} />
									</button>
								)}
							</div>
						))}
					</div>
				)}

				{displayError && <span className={`${baseClass}__error`}>{displayError}</span>}
			</div>
		)
	}
)

FileInput.displayName = 'FileInput'

export default FileInput
